import { ButtonGroup, Flex } from "@chakra-ui/react"
import * as React from "react"
import {
  FaCog,
  FaShare,
  FaMicrophone,
  FaMicrophoneSlash,
  FaThLarge,
  FaUserPlus,
  FaVideo,
  FaVideoSlash
} from "react-icons/fa"
import { useRecoilValue, useSetRecoilState, useRecoilState } from "recoil"
import { Icon } from "../../common/icons"
import { Views } from "../../../constants"
import { SDKContext } from "../../../context/SDKContext"
import { alertSelector } from "../../../recoil/atoms/alert"
import { screenShareUserSelector } from "../../../recoil/atoms/screenShareUser"
import { allUsersCountSelector } from "../../../recoil/atoms/users"
import { viewSelector } from "../../../recoil/atoms/view"
import {
  cameraPermissionStateAtom,
  microphonePermissionStateAtom,
  settingsMuteSelector
} from "../../../recoil/atoms/settings"
import { Alert } from "../../common/alert"
import LocalButton from "./_localButton"
import { StartScreenButtons } from "./start-screen-buttons"
import { useBoolean } from "../../../hooks/useBoolean"
import { useClickOutside } from "../../../hooks/useClickOutside"
import SettingsContainer from "../../settings/settingsContainer"
import Modal from "../../common/modal"
import {
  watchPartyScreenLinkSelector,
  watchPartyScreenSelector,
  watchPartyHostIdAtom
} from "../../../recoil/atoms/watchPartyScreen"
import { brandingSelector } from "../../../recoil/atoms/room"
import { IoExitSharp } from "react-icons/io5"
import { pipParamsSelector } from "../../../recoil/atoms/pictureInPicture"
import { usePictureInPicture } from "../../../hooks/usePictureInPicture"
import { useTranslation } from "react-i18next"
import { taggedBadgesAtom } from "../../../recoil/atoms/taggedBadges"
import { shoppableMNPVideoAtom } from "../../../recoil/atoms/shoppableVideos"
import { showNoVideoSubscribeScreenSelector } from "../../../recoil/atoms/rtc"
import RecordButton from "./record-button"
import { blendWithWhiteBackground } from "../../../util"
import useDeviceManagerChecker from "../../../hooks/useDeviceManagerChecker"
import { DevicePermissionState } from "../../settings/settings.interfaces"
import StreamButton from "./stream-button"
import { whiteboardShareUserAtom } from "../../../recoil/atoms/whiteboard"
import useConfig from "src/hooks/useConfig"
import { AvatarMicButton } from "./mic-button"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import { navigatedFromAvatarsListAtom } from "src/recoil/atoms/avatars"
import { useHistory } from "react-router-dom"

const UserPermission = window.MeetOnline?.UserPermission

interface ButtonsLocalViewProps {
  isAudience?: boolean
  isBroadcaster?: boolean
  isWithoutTitle: boolean
  showSelecedStyling?: boolean
  numberOfUsers: number
  localUserProperties: any
  isHiddenControls: boolean
}

export const ButtonsLocalView: React.FC<ButtonsLocalViewProps> = React.memo(
  ({
    isAudience,
    isWithoutTitle,
    numberOfUsers,
    localUserProperties,
    isBroadcaster,
    isHiddenControls
  }) => {
    const config = useConfig()
    const { t } = useTranslation()
    const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
    const setIsOpen = useSetRecoilState(alertSelector)
    const roomStyles = useRecoilValue(brandingSelector)
    const [isMutedDevices, setIsMutedDevices] =
      useRecoilState(settingsMuteSelector)
    const [isOpenSettings, setIsOpenSettings] = useBoolean(false)
    const [isOpenShareModal, setIsOpenShareModal] = useBoolean(false)
    const { avatarId } = useCurrentAvatarInfo()
    const isAvatarEnabled = !!avatarId

    const { exitPictureInPicture } = usePictureInPicture()
    const { isActivePiP } = useRecoilValue(pipParamsSelector)
    const setPiPParams = useSetRecoilState(pipParamsSelector)
    const setTaggedBadges = useSetRecoilState(taggedBadgesAtom)
    const allUserCount = useRecoilValue(allUsersCountSelector)
    const screenShareUser = useRecoilValue(screenShareUserSelector)
    const meetingClient = React.useContext(SDKContext)
    const setView = useSetRecoilState(viewSelector)
    const microphonePermissionState = useRecoilValue(
      microphonePermissionStateAtom
    )
    const cameraPermissionState = useRecoilValue(cameraPermissionStateAtom)

    const settingRef = React.useRef<HTMLDivElement>(null)
    const shareRef = React.useRef<HTMLDivElement>(null)
    const isMediaDisabled = useRecoilValue(showNoVideoSubscribeScreenSelector)

    useClickOutside(settingRef, setIsOpenSettings.off)
    useClickOutside(shareRef, setIsOpenShareModal.off)

    const { toggleLocalCamera, toggleLocalMicrophone } =
      useDeviceManagerChecker()
    const setWatchPartyScreen = useSetRecoilState(watchPartyScreenSelector)
    const setWatchPartyScreenLink = useSetRecoilState(
      watchPartyScreenLinkSelector
    )
    const isWatchPartyInProgress = useRecoilValue(watchPartyScreenSelector)
    const wpHostId = useRecoilValue(watchPartyHostIdAtom)
    const navigatedFromAvatarsList = useRecoilValue(
      navigatedFromAvatarsListAtom
    )
    const history = useHistory()

    const toggleScreenShare = React.useCallback(() => {
      if (
        screenShareUser &&
        screenShareUser.uid &&
        screenShareUser.uid !== "ss" + localUserProperties.localUserId
      ) {
        setIsOpen(true)
      } else {
        meetingClient?.rtc?.requestScreenShare()
        setIsOpenShareModal.off()
      }
    }, [
      localUserProperties.localUserId,
      meetingClient?.rtc,
      screenShareUser,
      setIsOpen,
      setIsOpenShareModal
    ])

    const whiteboardShareUser = useRecoilValue(whiteboardShareUserAtom)

    const toggleWhiteboardShare = React.useCallback(() => {
      if (whiteboardShareUser != null) {
        console.log("request whiteboard share off")
        meetingClient?.rtc.requestWhiteboardShare(false)
        setIsOpenShareModal.off()
      } else {
        console.log("req whiteboard share")
        meetingClient?.rtc.requestWhiteboardShare(true)
        setIsOpenShareModal.off()
      }
    }, [
      localUserProperties.localUserId,
      meetingClient?.rtc,
      whiteboardShareUser,
      setIsOpen,
      setIsOpenShareModal
    ])

    const overwriteScreenShare = () => {
      setIsOpen(false)
      meetingClient?.rtc?.requestScreenShare()
    }

    const toggleWatchParty = React.useCallback(
      (src: string) => {
        meetingClient?.rtc?.toggleWatchParty(src)
      },
      [meetingClient?.rtc]
    )

    const handleStopWatchParty = React.useCallback(() => {
      if (isActivePiP) {
        exitPictureInPicture()
        setPiPParams({ isActivePiP: false })
      }
      meetingClient?.rtc?.stopWatchParty()
      meetingClient?.sendStoppingWatchParty(localUserProperties.localUserId)
      setShoppableMNPVideo({
        srcShoppableMNP: "",
        enabledShoppableMNP: false,
        idShoppableMNP: "",
        typeShoppableMNP: ""
      })
      setTaggedBadges([])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exitPictureInPicture, isActivePiP, meetingClient?.rtc, setPiPParams])

    const setterShareScreen = React.useMemo(() => {
      if (isAudience || isBroadcaster) {
        return toggleScreenShare
      }
      if (localUserProperties.screenShareEnabled) {
        return toggleScreenShare
      }
      if (
        isWatchPartyInProgress &&
        localUserProperties.localUserId === wpHostId
      ) {
        return handleStopWatchParty
      }

      return setIsOpenShareModal.toggle
    }, [
      isAudience,
      isBroadcaster,
      localUserProperties.screenShareEnabled,
      localUserProperties.localUserId,
      isWatchPartyInProgress,
      wpHostId,
      setIsOpenShareModal.toggle,
      toggleScreenShare,
      handleStopWatchParty
    ])

    const isShareScreen = React.useMemo(
      () =>
        localUserProperties.screenShareEnabled ||
        (isWatchPartyInProgress &&
          localUserProperties.localUserId === wpHostId),
      [
        localUserProperties.screenShareEnabled,
        localUserProperties.localUserId,
        isWatchPartyInProgress,
        wpHostId
      ]
    )

    const showByePage = React.useCallback(async () => {
      // let mediaTracks = meetingClient?.getMediaTracks(
      //   localUserProperties.localUserId
      // )

      // if(mediaTracks?.audioTrack){
      // mediaTracks?.audioTrack.setEnabled(false)
      // }
      // if (mediaTracks?.videoTrack) {
      //   mediaTracks?.videoTrack.setEnabled(false)
      // }

      setWatchPartyScreen(false)
      setWatchPartyScreenLink("")
      await meetingClient?.disconnectGateway(0)
      if (config.postLoginRedirectUrl === "") {
        setView(Views.BYE)
      } else {
        history.replace("/avatars") // TODO: use a config here. the problem with doing it right now is that it has the value 'loading' so we get taken to /loading. Could turn this into a useEffect that reacts when the config value is not 'loading' but we should just move the loading state outside of the config value / use a promise
      }
      setIsMutedDevices({ video: false, audio: false })

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      meetingClient,
      setIsMutedDevices,
      setView,
      setWatchPartyScreen,
      setWatchPartyScreenLink
    ])

    const isButtonHidden = React.useCallback(
      (p: number) => {
        return (localUserProperties?.permissions & p) === 0
      },
      [localUserProperties?.permissions]
    )

    const toggleMicrophone = React.useCallback(async () => {
      const isSuccess = await toggleLocalMicrophone()
      isSuccess && setIsMutedDevices({ audio: !isMutedDevices.audio })
    }, [toggleLocalMicrophone, isMutedDevices.audio, setIsMutedDevices])

    const toggleVideo = React.useCallback(async () => {
      const isSuccess = await toggleLocalCamera()
      isSuccess && setIsMutedDevices({ video: !isMutedDevices.video })
    }, [toggleLocalCamera, isMutedDevices.video, setIsMutedDevices])

    const toggleSetting = React.useCallback(() => {
      setIsOpenSettings.toggle()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isDisabledMicrophone = React.useMemo<boolean>(() => {
      // if we're in an avatar enabled room, the behaviour changes
      if (roomStyles.enableDigitalAvatar) return false

      return isButtonHidden(UserPermission.canUseMic) || isMediaDisabled
    }, [isButtonHidden, isMediaDisabled])

    const isDisabledCamera = React.useMemo<boolean>(() => {
      return isButtonHidden(UserPermission.canUseCam) || isMediaDisabled
    }, [isButtonHidden, isMediaDisabled])

    const isShareButtonDisabled = React.useMemo<boolean>(() => {
      if (isMediaDisabled) {
        return true
      }

      if (isAudience || isBroadcaster) {
        if (
          isWatchPartyInProgress &&
          localUserProperties.localUserId !== wpHostId &&
          wpHostId !== "modNotPresentImage" &&
          wpHostId !== "modNotPresentVideo" &&
          wpHostId !== "modNotPresentOverlay"
        ) {
          return true
        }
        return isButtonHidden(UserPermission.canUseScreenshare)
      } else if (allUserCount < 1) {
        return true
      } else if (
        wpHostId === "modNotPresentImage" ||
        wpHostId === "modNotPresentVideo" ||
        wpHostId === "modNotPresentOverlay"
      ) {
        return false
      } else if (
        isWatchPartyInProgress &&
        localUserProperties.localUserId !== wpHostId
      ) {
        return true
      } else {
        return false
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isButtonHidden,
      isBroadcaster,
      isAudience,
      isWatchPartyInProgress,
      wpHostId,
      isMediaDisabled
    ])

    const stylesButton = React.useMemo(() => {
      return { borderRadius: "0" }
    }, [])

    const stylesLeftSideButton = React.useMemo(() => {
      return {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
      }
    }, [])

    const stylesModal = React.useMemo(() => {
      return {
        wrapperStyles: {
          overflow: "visible",
          background: blendWithWhiteBackground(
            roomStyles?.servicePanelElementModalBackground
          ),
          boxShadow: `0 0 15px ${
            roomStyles?.servicePanelElementBorder ?? "#e2e8f0"
          }`
        }
      }
    }, [
      roomStyles?.servicePanelElementBorder,
      roomStyles?.servicePanelElementModalBackground
    ])

    const dataMicrophoneIcon = React.useMemo(() => {
      return {
        icons: [<Icon src={FaMicrophoneSlash} />, <Icon src={FaMicrophone} />],
        names: [t("Unmute"), t("Mute")]
      }
    }, [t])

    const dataScreenShareIcon = React.useMemo(() => {
      return {
        icons: [<Icon src={FaShare} />],
        names: [t("Share"), t("Stop sharing")]
      }
    }, [t])

    const dataVideoIcon = React.useMemo(() => {
      return {
        icons: [<Icon src={FaVideoSlash} />, <Icon src={FaVideo} />],
        names: [t("Start"), t("Stop")]
      }
    }, [t])

    const dataSettingIcon = React.useMemo(() => {
      return {
        icons: [<Icon src={FaCog} />],
        names: [t("Settings")]
      }
    }, [t])

    const dataLeaveIcon = React.useMemo(() => {
      return {
        icons: [<Icon src={IoExitSharp} width="1.1rem" height="1.1rem" />],
        names: [t("Leave")]
      }
    }, [t])

    // Hiding full component if user doesn't have permission
    if (isHiddenControls) return null

    const muteEnabled = config.localMuteEnabled === true
    const camEnabled = config.localCamEnabled === true
    const recordEnabled = config.localRecordEnabled === true
    const settingsEnabled = config.localSettingsEnabled === true
    const shareEnabled = config.localShareEnabled === true
    const streamEnabled = config.localStreamEnabled === true

    return (
      <Flex alignItems="flex-end">
        <ButtonGroup
          size="md"
          isAttached
          variant="outline"
          width="100%"
          marginTop={!isHiddenControls && "16px"}
          justifyContent="center"
          color={"gray.500"}
          zIndex="1000000"
          position="relative"
          className={isAudience && "buttons-local"}
        >
          {muteEnabled &&
            (isAvatarEnabled ? (
              <AvatarMicButton
                id="1"
                isFirst={true}
                isLast={false}
                isWithoutTitle={isWithoutTitle}
              />
            ) : (
              <LocalButton
                id="1"
                names={dataMicrophoneIcon.names}
                icons={dataMicrophoneIcon.icons}
                enabled={localUserProperties?.micEnabled}
                setter={toggleMicrophone}
                isFirst={true}
                isLast={false}
                isWithoutTitle={isWithoutTitle}
                // customstyles={stylesLeftSideButton}
                isDisabled={isDisabledMicrophone}
                showSelecedStyling={false}
                showWarningIcon={
                  microphonePermissionState === DevicePermissionState.denied
                }
              />
            ))}

          {camEnabled && (
            <LocalButton
              id="2"
              names={dataVideoIcon.names}
              icons={dataVideoIcon.icons}
              enabled={localUserProperties?.camEnabled}
              setter={toggleVideo}
              isWithoutTitle={isWithoutTitle}
              isFirst={!muteEnabled}
              isLast={false}
              isDisabled={isDisabledCamera}
              showSelecedStyling={false}
              showWarningIcon={
                cameraPermissionState === DevicePermissionState.denied
              }
            />
          )}

          {settingsEnabled && (
            <Flex
              position="relative"
              ref={settingRef}
              width="calc((66.66vw - 100px) / 7)"
            >
              <Modal
                isOutsideClickEnabled={false}
                isOpen={isOpenSettings}
                onClose={setIsOpenSettings.off}
                customstyles={stylesModal}
              >
                <SettingsContainer isShowFullView isHomePage={false} />
              </Modal>
              <LocalButton
                id="3"
                names={dataSettingIcon.names}
                icons={dataSettingIcon.icons}
                setter={toggleSetting}
                isActive={isOpenSettings}
                isWithoutTitle={isWithoutTitle}
                isFirst={!muteEnabled && !camEnabled}
                isLast={false}
                showSelecedStyling={false}
                haveModal={true}
                isDisabled={isDisabledCamera && isDisabledMicrophone}
              />
            </Flex>
          )}

          {shareEnabled && (
            <Flex
              position="relative"
              ref={shareRef}
              width="calc((66.66vw - 100px) / 7)"
            >
              <Modal
                isOutsideClickEnabled={false}
                isOpen={isOpenShareModal}
                onClose={setIsOpenShareModal.off}
                customstyles={{
                  wrapperStyles: {
                    background: blendWithWhiteBackground(
                      roomStyles?.servicePanelElementModalBackground
                    ),
                    boxShadow: `0 0 15px ${
                      roomStyles?.servicePanelElementBorder ?? "#e2e8f0"
                    }`
                  }
                }}
              >
                <StartScreenButtons
                  screenShareEnabled={localUserProperties?.screenShareEnabled}
                  toggleScreenShare={toggleScreenShare}
                  toggleWhiteboardShare={toggleWhiteboardShare}
                  closeModal={setIsOpenShareModal.off}
                  toggleWatchParty={toggleWatchParty}
                />
              </Modal>
              <LocalButton
                id="4"
                names={dataScreenShareIcon.names}
                icons={dataScreenShareIcon.icons}
                setter={setterShareScreen}
                enabled={isShareScreen}
                isActive={isOpenShareModal}
                isWithoutTitle={isWithoutTitle}
                isFirst={!muteEnabled && !camEnabled && !settingsEnabled}
                isLast={false}
                isDisabled={isShareButtonDisabled}
                showSelecedStyling={isShareScreen ? true : false}
                haveModal={true}
              />
            </Flex>
          )}

          {streamEnabled && (
            <StreamButton
              isWithoutTitle={isWithoutTitle}
              localUserProperties={localUserProperties}
              isFirst={
                !muteEnabled && !camEnabled && !settingsEnabled && !shareEnabled
              }
              isLast={false}
            />
          )}

          {recordEnabled && (
            <RecordButton
              isWithoutTitle={isWithoutTitle}
              localUserProperties={localUserProperties}
              isFirst={
                !muteEnabled &&
                !camEnabled &&
                !settingsEnabled &&
                !shareEnabled &&
                !streamEnabled
              }
              isLast={false}
            />
          )}

          <LocalButton
            id="7"
            names={["Change View"]}
            icons={[<FaThLarge />]}
            isHidden={true}
            isLast={false}
            isWithoutTitle={isWithoutTitle}
            isFirst={false}
          />

          <LocalButton
            id="8"
            names={["Invite"]}
            icons={[<FaUserPlus />]}
            isHidden={true}
            isLast={false}
            isWithoutTitle={isWithoutTitle}
          />

          <LocalButton
            id="9"
            names={dataLeaveIcon.names}
            icons={dataLeaveIcon.icons}
            handler={showByePage}
            isLast={true}
            isFirst={
              !muteEnabled &&
              !camEnabled &&
              !settingsEnabled &&
              !shareEnabled &&
              !streamEnabled &&
              !recordEnabled
            }
            isWithoutTitle={isWithoutTitle}
            showSelecedStyling={false}
          />
        </ButtonGroup>
        <Alert
          title="Screenshare in use"
          description="Someone else is currently sharing their screen. Would you like to share your screen instead?"
          cancelButtonName="Cancel"
          actionButtonName="Share"
          actionFunction={overwriteScreenShare}
        />
      </Flex>
    )
  }
)
export default ButtonsLocalView
